/**
 * V166.2 — Diagnostic Funnel "Máš eshop, nemáš objednávky?"
 *
 * Flow:
 *   1. Hero (URL + email) → submit → POST /api/diagnostic-funnel/scan
 *   2. Loading scanner animation (cycling through "checks" while we wait)
 *   3. Report screen — TOP findings in the worst category + prescribed
 *      3-agent bundle with 50% off (3 months) CTA
 *   4. Click "Aktivovať bundle" → POST /api/diagnostic-funnel/checkout
 *      → window.location = Stripe URL
 *
 * Copywriting tone: professional, business-outcome-driven urgency.
 * NO get-rich-quick / cash photos. Audience = skeptical e-shop owners.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Loader2, ShieldAlert, TrendingDown, AlertTriangle, CheckCircle2,
  Zap, ArrowRight, Lock, Clock, Search, Eye,
} from "lucide-react";
import { DiagnosticExitIntent } from "../components/common/DiagnosticExitIntent";

const CATEGORY_META = {
  visibility:   { label: "Viditeľnosť (SEO / GEO)",       Icon: Eye,           color: "text-amber-400" },
  conversion:   { label: "Konverzia (košík / dôvera)",    Icon: TrendingDown,  color: "text-red-400" },
  ops:          { label: "Prevádzka (admin / compliance)",Icon: ShieldAlert,   color: "text-violet-400" },
};

// Loading-scan steps shown one-by-one to make the wait feel substantial.
const SCAN_STEPS = [
  "Sťahujem HTML…",
  "Hľadám SEO / Schema.org tagy…",
  "Skenujem checkout & košík friction…",
  "Overujem GDPR & cookie banner…",
  "Hľadám trust signály (recenzie, záruky)…",
  "Vyhodnocujem dominantný problém…",
];

export default function DiagnosticFunnelPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ url: "", email: user?.email || "" });
  const [phase, setPhase] = useState("hero"); // hero | scanning | report
  const [scanStep, setScanStep] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({ total_scans: 0, scans_today: 0 });
  const [checkingOut, setCheckingOut] = useState(false);
  const [personalizedRec, setPersonalizedRec] = useState(null);
  const scanTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/diagnostic-funnel/stats`).then((r) => setStats(r.data || {})).catch(() => {});
    if (user?.email && !form.email) setForm((f) => ({ ...f, email: user.email }));
    // Pre-fill URL from query param (used when returning via exit-capture magic link)
    const prefillUrl = sp.get("u");
    if (prefillUrl) {
      try { setForm((f) => ({ ...f, url: decodeURIComponent(prefillUrl) })); } catch { /* noop */ }
    }
    // V167.4 — Personalized recommendation from Day-5 challenge email.
    // URL: /diagnostika-eshopu?personalized=1&bundle=conversion&email=...
    // We pre-fill email and fetch the user's pain-dimension breakdown so the
    // hero banner can show "Based on your 5-day audit, we recommend..."
    const personalized = sp.get("personalized");
    const prefillEmail = sp.get("email");
    const prefillBundle = sp.get("bundle");
    if (personalized && prefillEmail) {
      setForm((f) => ({ ...f, email: prefillEmail }));
      axios.get(`${API}/challenge/recommendation`, { params: { email: prefillEmail } })
        .then((r) => setPersonalizedRec({ ...r.data, bundleHint: prefillBundle }))
        .catch(() => setPersonalizedRec({ bundle_id: prefillBundle || "conversion", reason: "fallback" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleScan = async (e) => {
    e?.preventDefault();
    setError("");
    if (!form.url.trim()) { setError("Zadaj URL svojho e-shopu"); return; }
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) { setError("Zadaj platný email"); return; }
    setPhase("scanning");
    setScanStep(0);
    // Step animation runs independently of network request
    let i = 0;
    scanTimer.current = setInterval(() => {
      i = Math.min(i + 1, SCAN_STEPS.length - 1);
      setScanStep(i);
    }, 750);
    try {
      const r = await axios.post(`${API}/diagnostic-funnel/scan`, {
        url: form.url.trim(),
        email: form.email.trim(),
        language: language || "sk",
      });
      // Hold at least ~3.5s so the animation doesn't snap
      await new Promise((res) => setTimeout(res, 1200));
      clearInterval(scanTimer.current);
      setReport(r.data);
      setPhase("report");
    } catch (err) {
      clearInterval(scanTimer.current);
      setError(err?.response?.data?.detail || "Nepodarilo sa naskenovať túto adresu. Skús inú.");
      setPhase("hero");
    }
  };

  const handleCheckout = async () => {
    if (!report?.prescribed_bundle) return;
    setCheckingOut(true);
    try {
      const r = await axios.post(`${API}/diagnostic-funnel/checkout`, {
        bundle_id: report.prescribed_bundle.id,
        email: form.email.trim(),
        language: language || "sk",
        scan_id: report.scan_id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Checkout zlyhal. Skús znova.");
      setCheckingOut(false);
    }
  };

  // ============================== HERO ==============================
  if (phase === "hero") {
    return (
      <div className="min-h-screen bg-[var(--color-bg,#0a0a0f)] text-white">
        <SimpleHeader />
        <DiagnosticExitIntent language={language || "sk"} currentUrl={form.url} />
        <div className="max-w-4xl mx-auto px-6 pt-10 pb-20">
          {personalizedRec && <PersonalizedBanner rec={personalizedRec} onCheckout={async () => {
            setCheckingOut(true);
            try {
              const r = await axios.post(`${API}/diagnostic-funnel/checkout`, {
                bundle_id: personalizedRec.bundle_id || personalizedRec.bundleHint || "conversion",
                email: (form.email || sp.get("email") || "").trim(),
                language: language || "sk",
                origin_url: window.location.origin,
              });
              window.location.href = r.data.url;
            } catch (err) {
              setError(err?.response?.data?.detail || "Checkout zlyhal.");
              setCheckingOut(false);
            }
          }} checkingOut={checkingOut}/>}
          <div className="text-center mb-8" data-testid="diag-hero">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
              <AlertTriangle className="w-3 h-3" /> Diagnostika pre e-shopy
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5 leading-[1.05]">
              Máš e-shop. <span className="text-red-400">Nemáš objednávky.</span>
              <br/>
              <span className="text-white/70 text-3xl sm:text-4xl lg:text-5xl">Za 60 sekúnd ti ukážem prečo.</span>
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
              Bez registrácie. Bez kontaktovania predajcu. Bezplatne.
              Naskenujeme tvoj e-shop a pomenujeme <span className="text-white font-bold">3 konkrétne diery</span>,
              cez ktoré ti uniká obrat.
            </p>
          </div>

          {/* Live stats strip */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/50 mb-8" data-testid="diag-stats">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/> {stats.scans_today || 0} skenov dnes</span>
            <span>•</span>
            <span>{(stats.total_scans || 0).toLocaleString("sk-SK")} celkovo</span>
            <span>•</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3"/> ~60 sek</span>
          </div>

          {/* Form */}
          <form onSubmit={handleScan} className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 backdrop-blur-sm" data-testid="diag-form">
            <label className="block text-xs text-white/50 font-mono uppercase tracking-wider mb-2">URL e-shopu</label>
            <input
              data-testid="diag-url-input"
              type="text"
              autoFocus
              placeholder="https://moje-eshop.sk"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-white/15 rounded-lg text-white placeholder-white/30 focus:border-red-400 focus:outline-none mb-4"
            />
            <label className="block text-xs text-white/50 font-mono uppercase tracking-wider mb-2">Email (kam pošleme report)</label>
            <input
              data-testid="diag-email-input"
              type="email"
              placeholder="ty@firma.sk"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-white/15 rounded-lg text-white placeholder-white/30 focus:border-red-400 focus:outline-none mb-4"
            />
            {error && <div className="text-red-400 text-sm mb-3" data-testid="diag-error">{error}</div>}
            <button
              data-testid="diag-scan-button"
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-base transition-colors"
            >
              <Search className="w-5 h-5"/> Spustiť diagnostiku
            </button>
            <p className="text-[11px] text-white/40 mt-3 text-center inline-flex items-center justify-center gap-1.5 w-full">
              <Lock className="w-3 h-3"/> Sken je read-only. Nič na tvojom e-shope nezmeníme.
            </p>
          </form>

          {/* Benefits row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
            {[
              { Icon: Eye,          title: "Viditeľnosť",  text: "SEO / GEO / AI search audit" },
              { Icon: TrendingDown, title: "Konverzia",    text: "Checkout, košík, trust" },
              { Icon: ShieldAlert,  title: "Prevádzka",    text: "GDPR, faktúry, reklamácie" },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <Icon className="w-5 h-5 text-white/70 mb-2"/>
                <div className="font-bold text-sm mb-0.5">{title}</div>
                <div className="text-xs text-white/50">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================== SCANNING ==============================
  if (phase === "scanning") {
    return (
      <div className="min-h-screen bg-[var(--color-bg,#0a0a0f)] text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full" data-testid="diag-scanning">
          <div className="flex items-center justify-center mb-6">
            <Loader2 className="w-12 h-12 text-red-400 animate-spin"/>
          </div>
          <h2 className="text-2xl font-bold text-center mb-1">Skenujem tvoj e-shop…</h2>
          <p className="text-white/50 text-sm text-center mb-8 font-mono">{form.url}</p>
          <div className="space-y-2">
            {SCAN_STEPS.map((s, idx) => (
              <div
                key={s}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                  idx < scanStep ? "text-emerald-400" :
                  idx === scanStep ? "text-white bg-white/5" :
                  "text-white/30"
                }`}
                data-testid={`scan-step-${idx}`}
              >
                {idx < scanStep ? <CheckCircle2 className="w-4 h-4 shrink-0"/> :
                 idx === scanStep ? <Loader2 className="w-4 h-4 shrink-0 animate-spin"/> :
                 <div className="w-4 h-4 shrink-0 rounded-full border border-white/15"/>}
                <span className="text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================== REPORT ==============================
  if (phase === "report" && report) {
    const bundle = report.prescribed_bundle;
    const dominant = Object.entries(report.scores || {}).sort((a,b) => b[1]-a[1])[0]?.[0] || "visibility";
    const cat = CATEGORY_META[dominant];
    const findings = (report.findings?.[dominant] || []).slice(0, 5);
    const savings = Math.max(0, (bundle.regular_eur - bundle.promo_eur) * (bundle.promo_months || 3));

    return (
      <div className="min-h-screen bg-[var(--color-bg,#0a0a0f)] text-white">
        <SimpleHeader />
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-20" data-testid="diag-report">
          {/* Verdict */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
              <AlertTriangle className="w-3 h-3"/> Diagnostika dokončená
            </div>
            <p className="text-white/50 text-sm mb-1 font-mono break-all">{report.url}</p>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">{bundle.title}</h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">{bundle.subtitle}</p>
          </div>

          {/* Category Score Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-8" data-testid="diag-scorecards">
            {Object.entries(report.scores || {}).map(([key, score]) => {
              const meta = CATEGORY_META[key];
              const isDominant = key === dominant;
              return (
                <div
                  key={key}
                  className={`rounded-xl border p-3 sm:p-4 ${
                    isDominant ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-white/5"
                  }`}
                  data-testid={`score-${key}`}
                >
                  <meta.Icon className={`w-4 h-4 mb-2 ${meta.color}`}/>
                  <div className="text-xs text-white/50 mb-1">{meta.label}</div>
                  <div className={`text-2xl font-black ${isDominant ? "text-red-400" : "text-white/80"}`}>{score}<span className="text-sm text-white/40">/100</span></div>
                  <div className="text-[10px] text-white/40 mt-0.5">Pain skóre</div>
                </div>
              );
            })}
          </div>

          {/* Top findings */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <cat.Icon className={`w-5 h-5 ${cat.color}`}/>
              <h2 className="text-lg font-bold">{cat.label} — TOP zistenia</h2>
            </div>
            <p className="text-red-300 text-sm font-mono mb-4">⚠ {bundle.problem}</p>
            <ul className="space-y-2" data-testid="diag-findings">
              {findings.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-white/80">
                  <span className="text-red-400 font-bold mt-0.5">{idx + 1}.</span>
                  <span>{f}</span>
                </li>
              ))}
              {findings.length === 0 && (
                <li className="text-white/50 text-sm">Tvoj e-shop v tejto kategórii skóruje slušne. Pozri si ostatné kategórie.</li>
              )}
            </ul>
          </div>

          {/* Bundle prescription */}
          <div className="relative bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30 rounded-2xl p-6 sm:p-8 mb-6" data-testid="diag-bundle">
            <div className="absolute -top-3 left-6 px-3 py-1 bg-emerald-500 text-black text-[10px] font-mono uppercase tracking-widest font-black rounded-full">
              Odporúčaný bundle
            </div>
            <h2 className="text-2xl font-black mb-2">3 agenti + audit, ktorí toto vyriešia</h2>
            <p className="text-white/60 text-sm mb-5">Customizovaný presne pre tvoj problém. Spustia sa do 48 hodín.</p>

            <div className="space-y-2 mb-6">
              {bundle.agents.map((a) => (
                <div key={a.slug} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-black/30 border border-white/10 rounded-lg">
                  <div className="flex items-center gap-3 min-w-0">
                    <Zap className="w-4 h-4 text-emerald-400 shrink-0"/>
                    <span className="font-bold text-sm truncate">{a.name}</span>
                  </div>
                  <span className="text-xs text-emerald-300 font-mono shrink-0">{a.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-black/30 border border-white/10 rounded-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0"/>
                  <span className="font-bold text-sm truncate">+ {bundle.plus}</span>
                </div>
                <span className="text-xs text-emerald-300 font-mono shrink-0">Bonus</span>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-black/40 border border-white/10 rounded-xl p-4 mb-5">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-black text-emerald-400">€{bundle.promo_eur.toFixed(2).replace(".", ",")}</span>
                <span className="text-sm text-white/50">/ mesiac</span>
                <span className="text-sm text-white/40 line-through ml-2">€{bundle.regular_eur}</span>
                <span className="text-xs text-emerald-300 font-mono ml-auto">−50%</span>
              </div>
              <div className="text-xs text-white/50">
                Prvé {bundle.promo_months} mesiace zľava cez kupón <span className="font-mono text-emerald-300">DIAGNOSTIKA50</span> · potom €{bundle.regular_eur}/mes · zrušiteľné kedykoľvek
              </div>
              <div className="text-xs text-emerald-300 font-mono mt-2">Ušetríš €{savings.toFixed(0)} za prvé 3 mesiace.</div>
            </div>

            {error && <div className="text-red-400 text-sm mb-3" data-testid="diag-checkout-error">{error}</div>}
            <button
              data-testid="diag-checkout-button"
              onClick={handleCheckout}
              disabled={checkingOut}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-black text-base transition-colors disabled:opacity-60"
            >
              {checkingOut ? (
                <><Loader2 className="w-5 h-5 animate-spin"/> Otváram bezpečnú platbu…</>
              ) : (
                <>Aktivovať bundle za €{bundle.promo_eur.toFixed(2).replace(".", ",")}/mes <ArrowRight className="w-5 h-5"/></>
              )}
            </button>
            <p className="text-[11px] text-white/40 mt-3 text-center inline-flex items-center justify-center gap-1.5 w-full">
              <Lock className="w-3 h-3"/> Platba cez Stripe · zľava sa aplikuje automaticky · zrušíš na 1 klik v dashboarde
            </p>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-white/40 text-center max-w-xl mx-auto">
            Diagnostika je heuristický sken (~60 sek). Pre kompletný technický audit s akčným plánom si po platbe spustíš plný sken v dashboarde.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function SimpleHeader() {
  return (
    <header className="border-b border-white/10 px-6 py-4">
      <a href="/" className="inline-flex items-center gap-2 text-white font-black text-lg tracking-tight">
        <span className="text-red-400">▲</span> TopRankPulse
      </a>
    </header>
  );
}

const BUNDLE_LABEL = {
  visibility: { title: "Bundle Viditeľnosť", reason: "Tvoje SEO/GEO/AI viditeľnosť mala najnižšie skóre počas výzvy." },
  conversion: { title: "Bundle Konverzia",   reason: "Tvoj košík a checkout flow mali najväčšie diery počas výzvy." },
  ops:        { title: "Bundle Prevádzka",   reason: "Tvoja administratíva a GDPR mali najväčšie nedostatky." },
};

function PersonalizedBanner({ rec, onCheckout, checkingOut }) {
  const id = rec?.bundle_id || "conversion";
  const meta = BUNDLE_LABEL[id] || BUNDLE_LABEL.conversion;
  const scoresAvailable = rec?.scores && rec.scan_count > 0;
  return (
    <div
      className="mb-10 bg-gradient-to-br from-emerald-500/15 to-amber-500/5 border border-emerald-500/40 rounded-2xl p-5 sm:p-6"
      data-testid="diag-personalized-banner"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">
          Na základe tvojich 5-dňových auditov
        </span>
        {scoresAvailable && (
          <span className="text-[10px] text-white/40 font-mono">
            ({rec.scan_count} skenov)
          </span>
        )}
      </div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-1">
        Odporúčaný: <span className="text-emerald-300">{meta.title}</span>
      </h2>
      <p className="text-white/65 text-sm sm:text-base mb-4">{meta.reason}</p>

      {scoresAvailable && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {Object.entries(rec.scores).map(([dim, score]) => (
            <div
              key={dim}
              className={`rounded-lg border p-3 ${
                dim === id ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/5"
              }`}
              data-testid={`personalized-score-${dim}`}
            >
              <div className="text-[10px] text-white/50 font-mono uppercase tracking-wider">{dim}</div>
              <div className={`text-xl font-black ${dim === id ? "text-emerald-300" : "text-white/70"}`}>
                {Math.round(score)}<span className="text-xs text-white/40">/100</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onCheckout}
        disabled={checkingOut}
        data-testid="diag-personalized-checkout"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm sm:text-base transition-colors disabled:opacity-60"
      >
        {checkingOut ? "Otváram bezpečnú platbu…" : (
          <>Aktivovať {meta.title} za €133,50/mes (−50 %) <span aria-hidden>→</span></>
        )}
      </button>
      <p className="text-[11px] text-white/40 mt-2.5">
        Kupón DIAGNOSTIKA50 sa aplikuje automaticky · Stripe · zrušíš na 1 klik
      </p>
    </div>
  );
}
